/**
 * SuggestionsPanel + SuggestionRow — the canonical "ranked suggestions feed"
 * surface. One textured panel (gradient wash + dot halftone + film grain) with a
 * plain-text header, an optional "X of Y" count chip, an optional minimize arrow
 * (collapses the panel to just its header + count), and a stack of ranked row
 * cards (numbered rank chip · title · optional primary accent · one-line detail ·
 * optional hover-dismiss × · circular "continue" arrow).
 *
 * This is the home-dashboard **Suggestions** design, lifted out of `PlayList` so
 * every "here's what's worth doing" feed reads identically — the home Play queue
 * AND the Agents panel's "Suggested agents & flows". Presentational + hook-free
 * (no `"use client"`): a server component can import it for static link rows
 * (`href`), while a client host can pass `onClick`/`onDismiss` from inside its
 * own client tree.
 *
 * The neutral hue + dialed-down dot opacity are baked in: a suggestions feed has
 * no section, so it drives the treatment with `--muted-foreground`, never the
 * indigo `--primary` fallback.
 */

import * as React from "react";
import type { CSSProperties } from "react";
import { RouterLink as Link } from "../../router";
import { ArrowRight, ChevronDown, X } from "../../icons";
import { Card } from "../../card";
import { SectionTitle } from "../section-title";
import { Button } from "../../button";
import { TextureOverlay, GrainOverlay } from "../grain-overlay";

export interface SuggestionsPanelProps {
  title: string;
  subtitle?: string;
  /** Renders a "{shown} of {total}" chip, but only when total > shown. */
  count?: { shown: number; total: number };
  /** Optional action rendered top-right beside the count chip (e.g. a dismiss ×). */
  headerAction?: React.ReactNode;
  /** Optional trailer line below the rows (e.g. "+N more in Play"). */
  footer?: React.ReactNode;
  /**
   * Minimize state (host-controlled). When true the panel collapses to just its
   * header + count chip. The arrow toggle only renders when onToggleCollapsed is
   * provided, and the host owns persistence so this block stays presentational.
   */
  collapsed?: boolean;
  /** Toggle handler for the minimize arrow. Omit to disable collapsing entirely. */
  onToggleCollapsed?: () => void;
  children: React.ReactNode;
}

export function SuggestionsPanel({
  title,
  subtitle,
  count,
  headerAction,
  footer,
  collapsed,
  onToggleCollapsed,
  children,
}: SuggestionsPanelProps) {
  // The minimize arrow only appears when the host opts in via onToggleCollapsed;
  // `collapsed` is host-controlled so the host can persist it (e.g. localStorage)
  // while this block stays presentational.
  const collapsible = onToggleCollapsed != null;
  const isCollapsed = collapsible && collapsed === true;
  // Expanded: the "{shown} of {total}" chip, only when the list is capped.
  // Collapsed: the bare total, so a minimized panel still announces how many
  // suggestions are waiting behind it.
  const showChip =
    count != null && (isCollapsed ? count.total > 0 : count.total > count.shown);
  return (
    <section
      style={
        {
          // No section → drive the texture with a NEUTRAL hue, not the indigo
          // --primary fallback. Dot halftone dialed right down to a whisper.
          ["--section" as string]: "var(--muted-foreground)",
          ["--texture-dots-opacity" as string]: "0.18",
        } as CSSProperties
      }
      className="section-shine-border relative isolate flex flex-col gap-3 overflow-hidden rounded-xl bg-card p-4"
    >
      {/* Gradient wash + halftone + film grain, kept VERY subtle (gradient at
          ~25% opacity) so it reads as a faint textured surface, not a tinted
          panel. All three layers sit behind the z-10 content, clipped to the
          rounded-lg corners. */}
      <div
        aria-hidden
        className="section-gradient pointer-events-none absolute inset-0 z-0 opacity-25"
      />
      <TextureOverlay variant="dots" />
      <GrainOverlay />

      {/* Title row through the canonical <SectionTitle> (variant="plain") so the
          suggestions feed titles identically to every other surface — same
          type scale, no bespoke type-title heading. The panel keeps its own
          identity below the title row (texture + ranked rows); the "x of y"
          chip + collapse arrow ride in the action slot. */}
      <SectionTitle
        variant="plain"
        size="md"
        title={title}
        subtitle={subtitle && !isCollapsed ? subtitle : undefined}
        className="relative z-10"
        action={
          showChip || headerAction || collapsible ? (
            <>
              {showChip ? (
                <span
                  className={[
                    "rounded-full px-2.5 py-1 type-body tabular-nums",
                    // Minimized: the count is the panel's only signal of waiting
                    // work, so it reads as a solid primary badge. Expanded: a
                    // quiet "x of y" progress chip.
                    isCollapsed
                      ? "bg-primary text-primary-foreground"
                      : "bg-secondary text-muted-foreground",
                  ].join(" ")}
                >
                  {isCollapsed ? count.total : `${count.shown} of ${count.total}`}
                </span>
              ) : null}
              {headerAction}
              {collapsible ? (
                <Button
                  size="icon"
                  variant="ghost"
                  aria-expanded={!isCollapsed}
                  aria-label={
                    isCollapsed ? "Expand suggestions" : "Minimize suggestions"
                  }
                  onClick={onToggleCollapsed}
                  className="shrink-0 text-muted-foreground"
                >
                  <ChevronDown
                    strokeWidth={1.75}
                    className={[
                      "size-4 transition-transform duration-200",
                      isCollapsed ? "" : "rotate-180",
                    ].join(" ")}
                  />
                </Button>
              ) : null}
            </>
          ) : undefined
        }
      />

      {!isCollapsed ? (
        <>
          <div className="relative z-10 flex flex-col gap-2.5">{children}</div>
          {footer ? (
            <p className="relative z-10 type-body text-muted-foreground">{footer}</p>
          ) : null}
        </>
      ) : null}
    </section>
  );
}

/**
 * Canonical label for a suggestion's kind, shown in the row's accent slot on
 * every "ranked suggestions" surface — the home Play feed, the Agents "Suggested
 * agents & flows" panel, and the Pulse fix panel — so a flow/agent/signal reads
 * identically across all three. `play` (a Pulse-only kind) renders as "Action".
 */
export const SUGGESTION_KIND_LABEL: Record<"flow" | "agent" | "play" | "job" | "tool", string> = {
  flow: "Flow",
  agent: "Agent",
  play: "Action",
  job: "Firing",
  tool: "Signal",
};

export interface SuggestionRowProps {
  /** 1-based rank — shown in the leading chip. */
  rank: number;
  title: string;
  /** Short primary-colored text beside the title (payoff / kind label). */
  accent?: React.ReactNode;
  /** One-line, truncated, muted secondary text. */
  detail?: string;
  /** Static link card — the whole row navigates here. Never combined with onDismiss. */
  href?: string;
  /** Interactive card — clicking the row fires this (the host opens a driver, etc.). */
  onClick?: () => void;
  /** Hover-revealed dismiss ×. Only valid alongside onClick (a button can't live in a link). */
  onDismiss?: () => void;
}

export function SuggestionRow({
  rank,
  title,
  accent,
  detail,
  href,
  onClick,
  onDismiss,
}: SuggestionRowProps) {
  const hasTarget = href != null || onClick != null;
  const body = (
    <>
      {/* Rank chip — reinforces "this is a ranked queue" without a per-row
          category icon. */}
      <span
        aria-hidden
        className="flex size-7 shrink-0 items-center justify-center rounded-full bg-secondary type-body font-medium tabular-nums text-foreground ring-1 ring-inset ring-border"
      >
        {rank}
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="truncate type-body font-medium text-foreground">{title}</span>
          {accent ? (
            <span className="shrink-0 type-body text-primary">{accent}</span>
          ) : null}
        </div>
        {detail ? (
          <p className="truncate type-body leading-body text-muted-foreground">{detail}</p>
        ) : null}
      </div>

      {onDismiss ? (
        <Button
          size="icon"
          variant="ghost"
          aria-label="Dismiss"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onDismiss();
          }}
          className="relative z-10 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
        >
          <X strokeWidth={1.75} className="h-3.5 w-3.5" />
        </Button>
      ) : null}
      {hasTarget ? (
        <span
          aria-hidden
          className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition-transform duration-200 group-hover:translate-x-0.5"
        >
          <ArrowRight strokeWidth={1.75} className="size-4" />
        </span>
      ) : null}
    </>
  );

  const rowClass = "group flex-row items-center gap-3 px-6 py-3";

  if (href != null) {
    return (
      <Card interactive asChild className={rowClass}>
        <Link href={href}>{body}</Link>
      </Card>
    );
  }
  if (onClick != null) {
    // Keyboard-operable: a bare clickable <div> is mouse-only. A native
    // <button> wrapper would illegally nest the onDismiss <button>, so use the
    // ARIA button pattern — focusable (tabIndex) + announced (role) + Enter/Space
    // fire onClick. (Card's interactive variant already paints the focus ring.)
    return (
      <Card
        interactive
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick();
          }
        }}
        className={rowClass}
      >
        {body}
      </Card>
    );
  }
  return <Card className={rowClass}>{body}</Card>;
}
